import { RotateCcw, TriangleAlert } from 'lucide-react';
import type { ReactElement, ReactNode } from 'react';

import { copyFor } from '../lib/errorCopy.js';

import { Button } from './Button.js';

/**
 * The failure surface, driven by `errorCopy` so no screen invents its own
 * wording for a code that already has some (criterion 18).
 *
 * The `requestId` is rendered in a collapsed `<details>`, never in the prose.
 * Someone raising a support ticket needs it; everyone else needs a sentence
 * they can act on, and a hex string in the middle of one is noise that makes
 * the whole message feel like a crash.
 *
 * It is a `.card` — the bright plane — for the same reason a table is: "we
 * could not look" is information, and it has to out-rank the recessed canvas it
 * sits on. The glyph moves out of the sentence and into a soft-tinted chip so
 * this is distinguishable from `EmptyState` at a glance, across the room,
 * before either sentence is read. The two states answer different questions and
 * looking alike is how an operator retries nothing and waits for data that was
 * never coming.
 */
export function ErrorState({
  error,
  title,
  body,
  onRetry,
  action,
}: {
  error?: unknown;
  title?: string;
  body?: string;
  onRetry?: () => void;
  action?: ReactNode;
}): ReactElement {
  const copy = error !== undefined ? copyFor(error) : null;
  const requestId =
    error && typeof error === 'object' && 'requestId' in error
      ? String((error as { requestId: unknown }).requestId)
      : '';

  return (
    <div role="alert" className="card flex items-start gap-[var(--space-4)] p-6">
      {/*
        The icon repeats in shape what the colour says, so the state does not
        depend on hue alone (WCAG 1.4.1) — this is the same icon+label pairing
        the status pills use. `danger-text` rather than `danger`: §16.6 needs
        4.5:1 and the pure token is 4.44:1 on this surface. The chip is the
        pill's own soft fill, so a failure here and a failed pill in a table are
        recognisably the same platform colour.
      */}
      <span
        aria-hidden="true"
        className="flex size-9 shrink-0 items-center justify-center rounded-[var(--radius-md)] bg-danger-soft text-danger-text"
      >
        <TriangleAlert size={18} strokeWidth={2} />
      </span>

      <div className="flex min-w-0 flex-col items-start gap-[var(--space-3)]">
        <div className="flex flex-col gap-[var(--space-1)]">
          <p className="text-[length:var(--text-sm)] font-medium text-body">
            {title ?? copy?.title ?? 'Something went wrong'}
          </p>
          <p className="max-w-prose text-[length:var(--text-sm)] text-muted">
            {body ?? copy?.body ?? ''}
          </p>
        </div>

        {onRetry ? (
          <Button variant="secondary" size="sm" icon={RotateCcw} onClick={onRetry}>
            {copy?.action ?? 'Try again'}
          </Button>
        ) : null}
        {action}

        {requestId ? (
          <details className="text-[length:var(--text-2xs)] text-muted">
            <summary className="cursor-pointer">Technical details</summary>
            <p className="mt-1 font-[family-name:var(--font-mono)] select-all">
              Reference: {requestId}
            </p>
          </details>
        ) : null}
      </div>
    </div>
  );
}
